import { useEffect, useState, type CSSProperties } from "react";
import { SelectField } from "../components/ui/select-field.js";

interface Country {
  code: string;
  label: string;
  re: RegExp;
  maxLen: number;
}

const COUNTRIES: Country[] = [
  { code: "+91",  label: "🇮🇳 +91",  re: /^[6-9][0-9]{9}$/,   maxLen: 10 },
  { code: "+1",   label: "🇺🇸 +1",   re: /^[2-9][0-9]{9}$/,   maxLen: 10 },
  { code: "+44",  label: "🇬🇧 +44",  re: /^7[0-9]{9}$/,        maxLen: 10 },
  { code: "+971", label: "🇦🇪 +971", re: /^5[0-9]{8}$/,        maxLen: 9  },
  { code: "+65",  label: "🇸🇬 +65",  re: /^[689][0-9]{7}$/,    maxLen: 8  },
  { code: "+61",  label: "🇦🇺 +61",  re: /^4[0-9]{8}$/,        maxLen: 9  },
  { code: "",     label: "Other",                re: /^.{4,15}$/,          maxLen: 15 },
];

const DEFAULT_CODE = "+91";

function parse(value: string | null | undefined, fallback: string): { code: string; digits: string } {
  const v = value ?? "";
  for (const c of COUNTRIES) {
    if (c.code && v.startsWith(c.code)) {
      return { code: c.code, digits: v.slice(c.code.length) };
    }
  }
  if (/^[6-9][0-9]{9}$/.test(v)) return { code: "+91", digits: v };
  return { code: fallback, digits: v };
}

export interface PhoneInputProps {
  value: string | null | undefined;
  onChange: (combined: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
  defaultCountry?: string;
}

export function PhoneInput({ value, onChange, disabled, style, defaultCountry = DEFAULT_CODE }: PhoneInputProps) {
  const parsed = parse(value, defaultCountry);
  const [code, setCode] = useState(parsed.code);
  const [digits, setDigits] = useState(parsed.digits);

  useEffect(() => {
    const p = parse(value, defaultCountry);
    setCode(p.code);
    setDigits(p.digits);
  }, [value, defaultCountry]);

  const country = COUNTRIES.find(c => c.code === code) ?? COUNTRIES[COUNTRIES.length - 1]!;
  const invalid = digits.length > 0 && !country.re.test(digits);

  const outerStyle: CSSProperties = {
    display: "flex",
    alignItems: "stretch",
    border: "1px solid var(--color-surface-200)",
    borderRadius: "var(--radius-md)",
    background: disabled ? "var(--color-surface-100)" : "var(--color-surface-0)",
    width: "100%",
    overflow: "hidden",
    boxSizing: "border-box",
    ...style,
  };

  const inputStyle: CSSProperties = {
    padding: "8px 10px",
    border: "none",
    background: "transparent",
    fontSize: "14px",
    outline: "none",
    flex: 1,
    minWidth: 0,
    cursor: disabled ? "not-allowed" : "text",
    color: disabled ? "var(--color-surface-400)" : "inherit",
  };

  function handleCodeChange(newCode: string) {
    setCode(newCode);
    onChange(newCode + digits);
  }

  function handleDigitsChange(newDigits: string) {
    const clean = newDigits.replace(/\D/g, "");
    setDigits(clean);
    onChange(code + clean);
  }

  return (
    <div>
      {/* The ring goes on the wrapper, via focus-within: the country SelectField
          and the number <input> are two controls inside one bordered box, so a
          ring on either alone would highlight half the field. The inner select's
          own ring is suppressed for the same reason -- and because the wrapper
          clips it to a hard edge with overflow: hidden. Same
          focus:ring-1/brand-500 treatment as Input, Textarea and SelectField. */}
      <div
        // !border-brand-500: outerStyle sets the border inline, and an inline
        // style beats a plain class, so the important modifier is what makes the
        // border join the ring instead of sitting dark between it and the field.
        className="focus-within:!border-brand-500 focus-within:ring-1 focus-within:ring-brand-500"
        style={outerStyle}
      >
        <SelectField
          size="sm"
          className="w-auto min-w-[88px] shrink-0 rounded-none border-0 border-r focus:ring-0 data-[state=open]:ring-0"
          value={code}
          onChange={e => handleCodeChange(e.target.value)}
          {...(disabled !== undefined ? { disabled } : {})}
        >
          {COUNTRIES.map(c => (
            <option key={c.code} value={c.code}>{c.label}</option>
          ))}
        </SelectField>
        <input
          type="tel"
          value={digits}
          onChange={e => handleDigitsChange(e.target.value)}
          disabled={disabled}
          maxLength={country.maxLen}
          placeholder={country.maxLen > 0 ? `${country.maxLen}-digit number` : "Phone number"}
          style={inputStyle}
        />
      </div>
      {invalid && (
        <div style={{ fontSize: 11, color: "var(--color-error)", marginTop: 3 }}>
          {code === "+91" ? "10 digits starting with 6–9" : code === "+1" ? "10-digit US/Canada number" : "Invalid phone number"}
        </div>
      )}
    </div>
  );
}
